import {
  clientDataFromReviewFields,
  formatDocumentTypeUsedLabel,
  isNoisyExtractedAddress,
  isNoisyExtractedName,
  maskSensitiveExtractedValue,
} from '../../utils/fundingClientIntakeUtils';

const API_BASE = '/api/funding-hq';
const EXTRACT_PATH = `${API_BASE}/documents/extract`;

export const EXTRACTION_STATUS = {
  UNAVAILABLE: 'extraction_unavailable',
  EXTRACTED: 'extracted',
  PARTIAL: 'partial',
  COMPLETE: 'complete',
  ERROR: 'extraction_error',
  SCANNING: 'scanning',
};

export const EXTRACTION_UNAVAILABLE_TITLE = 'Extraction service unavailable';

export const EXTRACTION_UNAVAILABLE_MESSAGE =
  'Extraction service unavailable. Continue manually.';

export const EXTRACTION_UNAVAILABLE_DETAIL =
  'The extraction service could not be reached or returned no usable data. You can still classify documents and enter client details manually.';

export const EXTRACTION_AUTH_REQUIRED_MESSAGE =
  'Authentication required. Please refresh and sign in again.';

export const EXTRACTION_NETWORK_MESSAGE =
  'Extraction service unavailable. Continue manually.';

export const EXTRACTION_DISABLED_MESSAGE =
  'Document extraction is disabled in this environment.';

export const EXTRACTION_PARTIAL_TITLE = 'Partial extraction. Review fields before creating client.';

export const EXTRACTION_PARTIAL_MESSAGE =
  'Some fields were extracted from your documents. Review and confirm values before creating the client.';

export const EXTRACTION_PARTIAL_TEXT_ONLY_MESSAGE =
  'Text was extracted from your documents, but structured fields could not be parsed. Review the upload or continue with manual intake.';

export const EXTRACTION_PARTIAL_MANUAL_HINT =
  'We found text but could not confidently place it into fields. Use the preview below to enter details manually.';

export const EXTRACTION_COMPLETE_TITLE = 'Extraction complete';

export const SAVE_DOCS_ONLY_REQUIRES_CLIENT_MESSAGE =
  'Save documents only requires an existing client. Choose Continue with manual intake to create a client first.';

/**
 * FundGenius HQ extraction is enabled by default.
 * Only an explicit REACT_APP_FUNDING_DOCUMENT_EXTRACTION_ENABLED=false disables it.
 */
export function isDocumentExtractionEnabled() {
  const flag = process.env.REACT_APP_FUNDING_DOCUMENT_EXTRACTION_ENABLED;
  if (flag == null || String(flag).trim() === '') {
    return true;
  }
  return String(flag).trim().toLowerCase() !== 'false';
}

function isExtractionDebugEnabled() {
  return process.env.NODE_ENV === 'development'
    || process.env.REACT_APP_FUNDING_DOCUMENT_EXTRACTION_DEBUG === 'true';
}

function debugLog(label, payload = {}) {
  if (!isExtractionDebugEnabled()) return;
  console.info(`[FundingDocs] ${label}`, payload);
}

function getAuthToken() {
  return (
    localStorage.getItem('fundelite_token')
    || localStorage.getItem('token')
    || localStorage.getItem('access_token')
    || localStorage.getItem('authToken')
  );
}

function backendBaseUrl() {
  const raw = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
  return raw.replace(/\/api$/, '');
}

function isExtractionEndpointMissing(error) {
  const status = error?.status;
  return status === 404 || status === 501 || status === 405 || status === 0;
}

function isAuthRequiredResponse(error) {
  const status = error?.status;
  return status === 401 || status === 403;
}

function parseErrorDetail(error) {
  const detail = error?.data?.detail;
  if (detail && typeof detail === 'object') {
    return detail.message || detail.error || null;
  }
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  return error?.message || null;
}

function isExtractionUnavailableResponse(error) {
  const status = error?.status;
  const detail = error?.data?.detail;
  if (status === 503) return true;
  if (detail && typeof detail === 'object' && detail.status === EXTRACTION_STATUS.UNAVAILABLE) {
    return true;
  }
  return false;
}

async function postExtractionFormData(formData, debugMeta = {}) {
  const token = getAuthToken();
  const url = `${backendBaseUrl()}${EXTRACT_PATH}`;

  debugLog('extraction request started', {
    url,
    fileCount: debugMeta.fileCount ?? null,
    fileNames: debugMeta.fileNames ?? [],
    hasAuthToken: Boolean(token),
  });

  let response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });
  } catch (error) {
    debugLog('extraction response', {
      ok: false,
      status: 0,
      error: error?.message || 'Network error',
    });
    const networkError = new Error(EXTRACTION_NETWORK_MESSAGE);
    networkError.status = 0;
    networkError.data = { message: EXTRACTION_NETWORK_MESSAGE };
    throw networkError;
  }

  const rawText = await response.text();
  let data = null;
  try {
    data = rawText ? JSON.parse(rawText) : null;
  } catch {
    data = { message: rawText };
  }

  debugLog('extraction response', {
    ok: response.ok,
    status: response.status,
    documentCount: Array.isArray(data?.documents) ? data.documents.length : 0,
    responseStatus: data?.status || null,
  });

  if (!response.ok) {
    const error = new Error(
      data?.detail?.message || data?.detail || data?.message || data?.error || `Request failed with status ${response.status}`,
    );
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
}

function buildClientFormData(extractionDocs) {
  const formData = new FormData();
  extractionDocs.forEach((doc, index) => {
    formData.append('files[]', doc.file, doc.fileName || `document-${index}`);
    formData.append(
      `documentMetadata[${index}]`,
      JSON.stringify({
        fileName: doc.fileName,
        documentTypes: doc.documentTypes,
        customName: doc.customName,
        context: 'client_intake',
      }),
    );
  });
  formData.append('source', 'fundgenius_hq');
  return formData;
}

export const EXTRACTION_IGNORED_OCR_WARNING =
  'Some OCR text was ignored because it did not meet field validation rules.';

function buildReviewFieldSource(sourceDocument, documentTypeUsed) {
  const fileName = sourceDocument || '—';
  const typeLabel = formatDocumentTypeUsedLabel(documentTypeUsed);
  return typeLabel ? `${fileName} · ${typeLabel}` : fileName;
}

function normalizeReviewField(entry, index) {
  const label = entry.label || entry.field || entry.fieldKey || 'Field';
  let extracted = entry.value ?? entry.extracted ?? '';
  if (entry.sensitive || label === 'SSN' || label === 'EIN') {
    extracted = maskSensitiveExtractedValue(label, extracted);
  }
  if (label === 'Personal Address' && isNoisyExtractedAddress(extracted)) {
    extracted = '';
  }
  if (['Owner Name', 'First Name', 'Last Name'].includes(label) && isNoisyExtractedName(extracted)) {
    extracted = '';
  }
  const documentTypeUsed = entry.documentTypeUsed
    || entry.document_type_used
    || entry.documentTypeDetected
    || entry.document_type_detected
    || null;
  return {
    id: entry.id || `field_${index + 1}`,
    field: label,
    fieldKey: entry.fieldKey || null,
    category: entry.category || 'Identity',
    current: '—',
    extracted,
    source: buildReviewFieldSource(entry.sourceDocument || entry.source, documentTypeUsed),
    documentTypeDetected: entry.documentTypeDetected || entry.document_type_detected || null,
    documentTypeUsed,
    confidence: entry.confidence ?? null,
    sensitive: Boolean(entry.sensitive),
    apply: entry.apply !== false && Boolean(extracted),
  };
}

function unavailableClientResult(extractionDocs, message, detail) {
  return {
    status: EXTRACTION_STATUS.UNAVAILABLE,
    fields: [],
    clientData: null,
    documents: extractionDocs.map((doc) => ({
      id: doc.id,
      fileName: doc.fileName,
      scanStatus: EXTRACTION_STATUS.UNAVAILABLE,
      extractionConfidence: null,
      extractedFields: [],
    })),
    message: message || EXTRACTION_UNAVAILABLE_MESSAGE,
    detail: detail || EXTRACTION_UNAVAILABLE_DETAIL,
  };
}

function mapDocumentScanStatus(doc) {
  if (doc.status === 'failed' || doc.status === 'error' || doc.status === 'unsupported') {
    return EXTRACTION_STATUS.ERROR;
  }
  if (doc.status === 'partial') {
    return EXTRACTION_STATUS.PARTIAL;
  }
  return EXTRACTION_STATUS.EXTRACTED;
}

function documentHasExtractedText(doc) {
  return Boolean(String(
    doc?.textPreview || doc?.text_preview || doc?.rawText || doc?.text || '',
  ).trim());
}

function normalizeClientExtraction(data, extractionDocs) {
  const documents = (data?.documents || []).map((doc, index) => {
    const sourceDoc = extractionDocs.find((item) => item.fileName === (doc.fileName || doc.filename))
      || extractionDocs[index];
    return {
      id: sourceDoc?.id,
      fileName: doc.fileName || doc.filename || sourceDoc?.fileName,
      scanStatus: mapDocumentScanStatus(doc),
      extractionConfidence: doc.confidence ?? null,
      extractedFields: doc.extractedFields || [],
      rawText: doc.rawText || doc.text,
      textPreview: doc.textPreview || doc.text_preview || (
        doc.text ? String(doc.text).slice(0, 500) : null
      ),
      documentTypeDetected: doc.documentTypeDetected || doc.document_type_detected || null,
      documentTypeUsed: doc.documentTypeUsed || doc.document_type_used || null,
      selectedDocumentType: doc.selectedDocumentType || doc.selected_document_type || null,
      warnings: doc.warnings || [],
    };
  });

  const fields = [];
  const aggregateWarnings = [];
  documents.forEach((doc) => {
    (doc.warnings || []).forEach((warning) => {
      if (warning && !aggregateWarnings.includes(warning)) {
        aggregateWarnings.push(warning);
      }
    });
    (doc.extractedFields || []).forEach((field) => {
      const normalized = normalizeReviewField({
        ...field,
        sourceDocument: field.sourceDocument || doc.fileName,
        documentTypeUsed: field.documentTypeUsed
          || field.document_type_used
          || doc.documentTypeUsed
          || doc.document_type_used,
      }, fields.length);
      if (normalized.extracted) {
        fields.push(normalized);
      }
    });
  });

  const hasPartialStatus = documents.some((doc) => doc.scanStatus === EXTRACTION_STATUS.PARTIAL)
    || data?.status === EXTRACTION_STATUS.PARTIAL;
  const hasExtractedText = documents.some(documentHasExtractedText);

  if (!fields.length) {
    if (hasPartialStatus || hasExtractedText) {
      const previewDoc = documents.find((doc) => doc.textPreview);
      return {
        status: EXTRACTION_STATUS.PARTIAL,
        fields: [],
        clientData: null,
        documents,
        message: data?.message || EXTRACTION_PARTIAL_TEXT_ONLY_MESSAGE,
        detail: EXTRACTION_PARTIAL_TITLE,
        textPreview: previewDoc?.textPreview || null,
        warnings: aggregateWarnings.length ? aggregateWarnings : documents.flatMap((doc) => doc.warnings || []),
      };
    }

    return unavailableClientResult(
      extractionDocs,
      data?.message || EXTRACTION_UNAVAILABLE_MESSAGE,
      data?.message || data?.detail || EXTRACTION_UNAVAILABLE_DETAIL,
    );
  }

  return {
    status: hasPartialStatus ? EXTRACTION_STATUS.PARTIAL : EXTRACTION_STATUS.EXTRACTED,
    fields,
    clientData: clientDataFromReviewFields(fields, {}),
    documents,
    message: hasPartialStatus ? EXTRACTION_PARTIAL_MESSAGE : null,
    detail: hasPartialStatus ? EXTRACTION_PARTIAL_TITLE : null,
    warnings: aggregateWarnings,
  };
}

function mapExtractionError(error, extractionDocs) {
  if (isAuthRequiredResponse(error)) {
    return {
      ...unavailableClientResult(
        extractionDocs,
        EXTRACTION_AUTH_REQUIRED_MESSAGE,
        EXTRACTION_AUTH_REQUIRED_MESSAGE,
      ),
      status: EXTRACTION_STATUS.ERROR,
    };
  }

  if (isExtractionUnavailableResponse(error)) {
    const reason = parseErrorDetail(error) || EXTRACTION_UNAVAILABLE_MESSAGE;
    return unavailableClientResult(extractionDocs, reason, reason);
  }

  if (isExtractionEndpointMissing(error)) {
    return unavailableClientResult(
      extractionDocs,
      EXTRACTION_NETWORK_MESSAGE,
      EXTRACTION_NETWORK_MESSAGE,
    );
  }

  return {
    ...unavailableClientResult(extractionDocs, error.message || 'Extraction failed'),
    status: EXTRACTION_STATUS.ERROR,
  };
}

export async function extractFromUploadedDocuments(documents = []) {
  const extractionDocs = documents.filter((doc) => doc.useForExtraction);
  if (!extractionDocs.length) {
    return unavailableClientResult([], 'Select at least one document for extraction.');
  }

  if (extractionDocs.some((doc) => !doc.file)) {
    return unavailableClientResult(extractionDocs);
  }

  if (!isDocumentExtractionEnabled()) {
    return unavailableClientResult(
      extractionDocs,
      EXTRACTION_DISABLED_MESSAGE,
      EXTRACTION_DISABLED_MESSAGE,
    );
  }

  try {
    const data = await postExtractionFormData(
      buildClientFormData(extractionDocs),
      {
        fileCount: extractionDocs.length,
        fileNames: extractionDocs.map((doc) => doc.fileName),
      },
    );
    return normalizeClientExtraction(data, extractionDocs);
  } catch (error) {
    return mapExtractionError(error, extractionDocs);
  }
}

function unavailableApplicationResult(fileName, message, detail) {
  return {
    status: EXTRACTION_STATUS.UNAVAILABLE,
    fields: {},
    extractionConfidence: {},
    bureauPullScores: null,
    selectedBureauPull: null,
    uploadedFileName: fileName,
    message: message || EXTRACTION_UNAVAILABLE_MESSAGE,
    detail: detail || EXTRACTION_UNAVAILABLE_DETAIL,
  };
}

function mapApplicationExtraction(doc) {
  const fields = {};
  const extractionConfidence = {};
  (doc.extractedFields || []).forEach((field) => {
    const key = field.fieldKey || field.field;
    if (!key) return;
    fields[key] = field.value;
    if (field.confidence != null) {
      extractionConfidence[key] = Number(field.confidence) >= 80 ? 'auto' : 'review';
    }
  });

  if (!Object.keys(fields).length) {
    if (documentHasExtractedText(doc)) {
      return {
        status: EXTRACTION_STATUS.PARTIAL,
        fields: {},
        extractionConfidence: {},
        bureauPullScores: doc.bureauPullScores || { experian: null, transunion: null, equifax: null },
        selectedBureauPull: doc.selectedBureauPull || null,
        rawText: doc.rawText || doc.text,
        uploadedFileName: doc.fileName || doc.filename,
        message: EXTRACTION_PARTIAL_TEXT_ONLY_MESSAGE,
        detail: EXTRACTION_PARTIAL_TITLE,
      };
    }
    return unavailableApplicationResult(doc.fileName || doc.filename);
  }

  return {
    status: EXTRACTION_STATUS.COMPLETE,
    fields,
    extractionConfidence,
    bureauPullScores: doc.bureauPullScores || { experian: null, transunion: null, equifax: null },
    selectedBureauPull: doc.selectedBureauPull || null,
    rawText: doc.rawText || doc.text,
    uploadedFileName: doc.fileName || doc.filename,
    message: null,
    detail: null,
  };
}

export async function extractApplicationDocument(file, fileName = '') {
  if (!file) {
    return unavailableApplicationResult(fileName);
  }

  const resolvedName = fileName || file.name || 'upload';

  if (!isDocumentExtractionEnabled()) {
    return unavailableApplicationResult(resolvedName, EXTRACTION_DISABLED_MESSAGE, EXTRACTION_DISABLED_MESSAGE);
  }

  try {
    const formData = new FormData();
    formData.append('files[]', file, resolvedName);
    formData.append('documentMetadata[0]', JSON.stringify({
      fileName: resolvedName,
      context: 'application',
    }));
    formData.append('source', 'fundgenius_hq');

    const data = await postExtractionFormData(formData, {
      fileCount: 1,
      fileNames: [resolvedName],
    });
    const doc = data?.documents?.[0];
    if (!doc) {
      return unavailableApplicationResult(resolvedName, data?.message, data?.message);
    }
    return mapApplicationExtraction(doc);
  } catch (error) {
    if (isAuthRequiredResponse(error)) {
      return {
        status: EXTRACTION_STATUS.ERROR,
        fields: {},
        extractionConfidence: {},
        bureauPullScores: null,
        selectedBureauPull: null,
        uploadedFileName: resolvedName,
        message: EXTRACTION_AUTH_REQUIRED_MESSAGE,
        detail: EXTRACTION_AUTH_REQUIRED_MESSAGE,
      };
    }
    if (isExtractionUnavailableResponse(error)) {
      const reason = parseErrorDetail(error) || EXTRACTION_UNAVAILABLE_MESSAGE;
      return unavailableApplicationResult(resolvedName, reason, reason);
    }
    if (isExtractionEndpointMissing(error)) {
      return unavailableApplicationResult(
        resolvedName,
        EXTRACTION_NETWORK_MESSAGE,
        EXTRACTION_NETWORK_MESSAGE,
      );
    }
    return {
      status: EXTRACTION_STATUS.ERROR,
      fields: {},
      extractionConfidence: {},
      bureauPullScores: null,
      selectedBureauPull: null,
      uploadedFileName: resolvedName,
      message: error.message || 'Extraction failed',
      detail: EXTRACTION_UNAVAILABLE_DETAIL,
    };
  }
}
